"use client";

import Link from "next/link";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowUpRight, Menu, X, Phone, ChevronDown } from "lucide-react";
import {
  primaryNav,
  servicesDropdown,
  primaryCta,
  contact,
} from "@/lib/site-nav";
import { Logo } from "./Logo";

export function MobileNav() {
  const [open, setOpen] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Lock body scroll when overlay is open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Close on Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="lg:hidden inline-flex items-center justify-center size-10 rounded-md border border-border-strong text-text transition hover:border-accent hover:text-accent"
        aria-label="Open menu"
        aria-expanded={open}
      >
        <Menu className="size-5" />
      </button>

      {open && mounted && createPortal(
        <div
          className="fixed inset-0 z-[100] lg:hidden flex flex-col"
          style={{ backgroundColor: "#000000" }}
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
        >
          <div className="site-container flex items-center justify-between py-4">
            <span onClick={() => setOpen(false)}>
              <Logo size="sm" />
            </span>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="inline-flex items-center justify-center size-10 rounded-md border border-border-strong text-text transition hover:border-accent hover:text-accent"
              aria-label="Close menu"
            >
              <X className="size-5" />
            </button>
          </div>

          <nav
            className="site-container flex-1 overflow-y-auto py-6"
            aria-label="Mobile primary"
          >
            <ul className="space-y-1">
              {primaryNav.map((item) => {
                if (item.label === "Services") {
                  return (
                    <li key={item.href}>
                      <details className="group/services">
                        <summary className="flex items-center justify-between cursor-pointer list-none py-3 text-h3 font-semibold text-text transition-colors hover:text-accent [&::-webkit-details-marker]:hidden">
                          <span>Services</span>
                          <ChevronDown
                            className="size-5 transition-transform duration-200 group-open/services:rotate-180"
                            aria-hidden="true"
                          />
                        </summary>
                        <ul className="mt-1 mb-3 ml-1 space-y-1 border-l border-border pl-4">
                          <li>
                            <Link
                              href={item.href}
                              onClick={() => setOpen(false)}
                              className="block py-2 text-body font-semibold text-accent transition-colors"
                            >
                              All services
                            </Link>
                          </li>
                          {servicesDropdown.map((sub, i) => (
                            <li key={sub.href}>
                              <Link
                                href={sub.href}
                                onClick={() => setOpen(false)}
                                className={[
                                  "block py-2 text-body transition-colors",
                                  i === 0
                                    ? "text-text font-semibold"
                                    : "text-text-muted hover:text-accent",
                                ].join(" ")}
                              >
                                {sub.label}
                                {i === 0 && (
                                  <span className="ml-2 inline-flex rounded-pill bg-accent/15 px-2 py-0.5 text-[0.625rem] font-medium uppercase tracking-wider text-accent">
                                    Pillar
                                  </span>
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </details>
                    </li>
                  );
                }
                return (
                  <li key={item.href}>
                    <Link
                      href={item.href}
                      onClick={() => setOpen(false)}
                      className="block text-h3 font-semibold py-3 text-text hover:text-accent"
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="site-container pb-8 pt-4 border-t border-border space-y-4">
            <Link
              href={primaryCta.href}
              onClick={() => setOpen(false)}
              className="inline-flex w-full items-center justify-center gap-2 rounded-pill bg-bg-soft border border-border-strong px-6 py-3.5 font-semibold text-text transition hover:border-accent hover:text-accent"
            >
              {primaryCta.label}
              <ArrowUpRight className="size-5" />
            </Link>
            <a
              href={contact.phoneMobileHref}
              className="inline-flex w-full items-center justify-center gap-2 rounded-pill border border-border-strong px-6 py-3.5 text-text transition hover:border-accent hover:text-accent"
            >
              <Phone className="size-4" />
              {contact.phoneMobile}
            </a>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
